import type { SyslogEventFragmentFragment } from '@api/gql/graphql';
import PrimarySecondary from '@common/ui/PrimarySecondary';
import { NoResults, TableWrapper } from '@components';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { formatText } from '@SystemLog/utils/formatText';
import { DateTime } from '@utils';
import { CustomAnalyticsEvents } from '@utils/analytics';

interface SystemLogTableProps {
  events: SyslogEventFragmentFragment[];
  isLoading?: boolean;
  onEventSelect: (eventId: string) => void;
  selectedEventId?: string | null;
}

export default function SystemLogTable({ events, isLoading, onEventSelect, selectedEventId }: SystemLogTableProps) {
  if (events.length === 0) {
    return <NoResults>No events found</NoResults>;
  }

  return (
    <TableWrapper loading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Actor</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => (
            <SystemLogTableRow
              key={event.id}
              event={event}
              onClick={() => onEventSelect(event.id)}
              selected={selectedEventId === event.id}
            />
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
}

function SystemLogTableRow({
  event,
  onClick,
  selected,
}: {
  event: SyslogEventFragmentFragment;
  onClick: () => void;
  selected?: boolean;
}) {
  const showActorId = event.actor.name.toLowerCase() !== event.actor.id.toLowerCase();
  return (
    <TableRow
      key={event.id}
      onClick={onClick}
      hover
      className="MuiTableRow-clickable"
      selected={selected}
      data-trigger={CustomAnalyticsEvents.SYSLOG_EVENT_CLICKED}
      data-props={JSON.stringify({
        action: event.action,
        targetType: event.target.type,
      })}
    >
      <TableCell>{DateTime.fromDate(new Date(event.timestamp)).readableWithSeconds}</TableCell>
      <TableCell>{formatText(event.action)}</TableCell>
      <TableCell>
        <PrimarySecondary primary={formatText(event.actor.name)} secondary={showActorId ? event.actor.id : undefined} />
      </TableCell>
      <TableCell>
        <PrimarySecondary primary={event.target.name} secondary={formatText(event.target.type)} />
      </TableCell>
      <TableCell>{formatText(event.source)}</TableCell>
    </TableRow>
  );
}
